import { useState, useEffect, useRef, useCallback } from "react";
import { media } from "@glitz/core";
import { styled, applyClassName } from "@glitz/react";
import { GrLinkPrevious } from "react-icons/gr";
import { spacing, black, green, white, fontSize, Theme } from "../theme";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { CheckoutSteps } from "../components/CheckoutSteps";
import Summary from "../components/Summary";
import OrderSummary from "../components/OrderSummary";
import ShippingOptions from "../components/ShippingOptions";
import ShippingMethodSelector from "../components/ShippingMethodSelector";
import TmaCheckoutShippingStep from "../components/TmaCheckoutShippingStep";
import Button from "../components/basic/Button";
import { fetchTmaStatus } from "../api/tmaShipping.api";
import Link from "../components/basic/Link";
import CheckoutPaymentStep from "../components/CheckoutPaymentStep";
import SwishOption from "../components/SwishOption";
import { trackPurchaseFromOrder } from "../lib/tracking";

const API_BASE = process.env.REACT_APP_API_URL || "";

type PayOrderLoadState = "idle" | "loading" | "ready" | "not_found" | "error";

type PayOrderSnapshot = {
  status: string;
  total: string;
  orderNumber?: string;
  payment_method?: string;
  payment_method_title?: string;
};

const updateOrderStatus = async (
  orderId: number | string,
  status: string,
  paymentMeta?: { payment_method?: string; payment_method_title?: string; transaction_id?: string }
) => {
  try {
    await fetch(`${API_BASE}/api/orders/${orderId}/status`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status, ...paymentMeta }),
    });
  } catch (e) {
    console.error("Failed to update order status:", e);
  }
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { orderId: orderIdParam } = useParams<{ orderId: string }>();
  const isPaymentStep = pathname.startsWith("/checkout/pay");

  const [currentStep, setCurrentStep] = useState<number>(isPaymentStep ? 3 : 1);
  const [orderId, setOrderId] = useState<number | string | null>(null);
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<"swish" | "stripe" | null>(null);
  const [orderTotal, setOrderTotal] = useState<string | null>(null);
  const [payOrderLoad, setPayOrderLoad] = useState<PayOrderLoadState>("idle");
  const [payOrderSnapshot, setPayOrderSnapshot] = useState<PayOrderSnapshot | null>(null);
  const [tmaShippingEnabled, setTmaShippingEnabled] = useState(false);
  const handledOrderRef = useRef<string | null>(null);
  const stripeReturnSyncRef = useRef<string | null>(null);
  const { getTotalPrice, cartItemCount, clearCart } = useCart();
  const totalPrice = getTotalPrice();

  const refreshPayOrderSnapshot = useCallback(async (oid: number | string) => {
    try {
      const res = await fetch(`${API_BASE}/api/orders/${oid}`);
      if (!res.ok) return;
      const data = await res.json();
      setPayOrderSnapshot({
        status: data.status,
        total: data.total,
        orderNumber: data.orderNumber,
        payment_method: data.payment_method,
        payment_method_title: data.payment_method_title,
      });
      if (data.orderNumber) setOrderNumber(String(data.orderNumber));
      setOrderTotal(data.total ?? null);
      setPayOrderLoad("ready");
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchTmaStatus()
      .then((s) => {
        if (!cancelled) setTmaShippingEnabled(Boolean(s.enabled));
      })
      .catch(() => {
        if (!cancelled) setTmaShippingEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!orderIdParam) return;
    const id = /^\d+$/.test(orderIdParam)
      ? Number(orderIdParam)
      : orderIdParam;
    setOrderId(id);
    const paymentIntentId = searchParams.get("payment_intent");
    const redirectStatus = searchParams.get("redirect_status");

    // Klarna/3DS: Stripe redirectar tillbaka hit. Visa bekräftelsesteget direkt
    // (inte betalningsformuläret) medan vi synkar orderstatus.
    if (paymentIntentId || redirectStatus === "succeeded") {
      setPaymentMethod("stripe");
      setCurrentStep(4);
    }

    if (paymentIntentId) {
      if (stripeReturnSyncRef.current === paymentIntentId) {
        return;
      }
      stripeReturnSyncRef.current = paymentIntentId;
      let cancelled = false;
      (async () => {
        let attempts = 0;
        while (!cancelled && attempts < 24) {
          const res = await fetch(`${API_BASE}/api/payments/stripe/sync-order`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ paymentIntentId }),
          });
          const data = await res.json().catch(() => ({}));
          if (res.ok && data.ok) {
            if (!cancelled) {
              handledOrderRef.current = orderIdParam;
              setPaymentMethod("stripe");
              setCurrentStep(4);
              await trackPurchaseFromOrder(id);
              clearCart();
              await refreshPayOrderSnapshot(id);
              setSearchParams({}, { replace: true });
            }
            return;
          }
          if (res.status === 202) {
            attempts += 1;
            await new Promise((r) => setTimeout(r, 1250));
            continue;
          }
          break;
        }
        if (
          !cancelled &&
          redirectStatus === "succeeded" &&
          handledOrderRef.current !== orderIdParam
        ) {
          handledOrderRef.current = orderIdParam;
          await updateOrderStatus(id, "processing", {
            payment_method: "stripe",
            payment_method_title: "Klarna/Stripe",
            transaction_id: paymentIntentId,
          });
          setPaymentMethod("stripe");
          setCurrentStep(4);
          await trackPurchaseFromOrder(id);
          clearCart();
          await refreshPayOrderSnapshot(id);
          setSearchParams({}, { replace: true });
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    if (redirectStatus === "succeeded" && handledOrderRef.current !== orderIdParam) {
      handledOrderRef.current = orderIdParam;
      setPaymentMethod("stripe");
      setCurrentStep(4);
      void trackPurchaseFromOrder(id);
      clearCart();
      void (async () => {
        await updateOrderStatus(id, "processing", {
          payment_method: "stripe",
          payment_method_title: "Klarna/Stripe",
          transaction_id: searchParams.get("payment_intent") || undefined,
        });
        await refreshPayOrderSnapshot(id);
        setSearchParams({}, { replace: true });
      })();
    }
  }, [
    orderIdParam,
    searchParams,
    setSearchParams,
    clearCart,
    refreshPayOrderSnapshot,
  ]);

  useEffect(() => {
    if (!orderIdParam) {
      setPayOrderLoad("idle");
      setPayOrderSnapshot(null);
      return;
    }
    const id = /^\d+$/.test(orderIdParam)
      ? Number(orderIdParam)
      : orderIdParam;
    setOrderId(id);
    const hasStripeReturn =
      Boolean(searchParams.get("payment_intent")) ||
      Boolean(searchParams.get("redirect_status"));

    let cancelled = false;
    (async () => {
      setPayOrderLoad("loading");
      try {
        const res = await fetch(`${API_BASE}/api/orders/${orderIdParam}`);
        if (cancelled) return;
        if (res.status === 404) {
          setPayOrderLoad("not_found");
          setPayOrderSnapshot(null);
          return;
        }
        if (!res.ok) {
          setPayOrderLoad("error");
          setPayOrderSnapshot(null);
          return;
        }
        const data = await res.json();
        const snap: PayOrderSnapshot = {
          status: data.status,
          total: data.total,
          orderNumber: data.orderNumber,
          payment_method: data.payment_method,
          payment_method_title: data.payment_method_title,
        };
        setPayOrderSnapshot(snap);
        if (data.orderNumber) setOrderNumber(String(data.orderNumber));
        setOrderTotal(data.total ?? null);
        setPayOrderLoad("ready");

        // Under Klarna/Stripe-retur styr sync-effekten steget — skriv inte över till 3.
        if (hasStripeReturn || handledOrderRef.current === orderIdParam) {
          return;
        }

        if (data.status === "pending") {
          setCurrentStep(3);
        } else if (
          data.status === "on-hold" &&
          String(data.payment_method || "").toLowerCase() === "swish"
        ) {
          // Swish bekräftad — visa Swish-uppgifter, inte betalningsvalet.
          setCurrentStep(4);
          setPaymentMethod("swish");
        } else if (data.status === "on-hold") {
          // on_hold utan Swish (t.ex. Flow-default) → låt kunden välja betalsätt.
          setCurrentStep(3);
        } else if (data.status === "processing") {
          setCurrentStep(4);
          const pm = String(data.payment_method || "").toLowerCase();
          setPaymentMethod(pm === "stripe" || pm === "swish" ? pm : null);
        } else if (data.status === "completed") {
          setCurrentStep(4);
          setPaymentMethod(null);
        } else if (["cancelled", "failed", "refunded"].includes(data.status)) {
          setCurrentStep(4);
          setPaymentMethod(null);
        }
      } catch {
        if (!cancelled) {
          setPayOrderLoad("error");
          setPayOrderSnapshot(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orderIdParam, searchParams]);

  const handleStepChange = (step: number) => {
    if (currentStep === 4) return;
    setCurrentStep(step);
  };

  // Efter adress/frakt m.m. sitter man ofta långt ner — scrolla upp till nästa steg.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [currentStep]);

  const onOrderCreated = ({
    orderId,
    orderNumber: createdNumber,
  }: {
    orderId: number | string;
    orderNumber?: string;
  }) => {
    setOrderId(orderId);
    if (createdNumber) setOrderNumber(String(createdNumber));
    setCurrentStep(3);
    navigate(`/checkout/pay/${orderId}`, { replace: true });
  };

  const displayOrderNumber =
    orderNumber ||
    payOrderSnapshot?.orderNumber ||
    (orderId != null ? String(orderId) : "");

  const hasStripeReturn =
    Boolean(searchParams.get("payment_intent")) ||
    Boolean(searchParams.get("redirect_status"));

  const showPayRouteNotFound = Boolean(orderIdParam) && payOrderLoad === "not_found";
  const showPayRouteError = Boolean(orderIdParam) && payOrderLoad === "error";
  const payRouteLoading =
    Boolean(orderIdParam) && payOrderLoad === "loading" && !hasStripeReturn;

  // Visa betalningsval för obetalda ordrar (pending, eller on-hold utan Swish).
  const needsPaymentChoice =
    payOrderSnapshot?.status === "pending" ||
    (payOrderSnapshot?.status === "on-hold" &&
      String(payOrderSnapshot?.payment_method || "").toLowerCase() !== "swish" &&
      paymentMethod !== "swish");

  const showPaymentStep =
    currentStep === 3 &&
    orderId &&
    !hasStripeReturn &&
    payOrderLoad === "ready" &&
    needsPaymentChoice;

  const renderPayConfirmation = () => {
    if (!orderId) return null;
    if (hasStripeReturn && (payOrderLoad === "loading" || payOrderLoad === "idle" || payOrderSnapshot?.status === "pending")) {
      return (
        <ConfirmationWrapper>
          <StatusMessage>Slutför betalning…</StatusMessage>
        </ConfirmationWrapper>
      );
    }
    if (payOrderLoad === "loading" || payOrderLoad === "idle") {
      return (
        <ConfirmationWrapper>
          <StatusMessage>Hämtar order…</StatusMessage>
        </ConfirmationWrapper>
      );
    }
    if (payOrderLoad === "error" || payOrderLoad === "not_found") return null;

    const s = payOrderSnapshot?.status;

    if (s === "cancelled") {
      return (
        <ConfirmationWrapper>
          <ConfirmationTitle>Order avbruten</ConfirmationTitle>
          <NoticeText>Denna order är avbruten.</NoticeText>
        </ConfirmationWrapper>
      );
    }

    if (s === "failed" || s === "refunded") {
      return (
        <ConfirmationWrapper>
          <ConfirmationTitle>Order avslutad</ConfirmationTitle>
          <NoticeText>
            Ordern kunde inte slutföras eller har återbetalats. Kontakta oss om du har frågor.
          </NoticeText>
        </ConfirmationWrapper>
      );
    }

    if (s === "completed") {
      return (
        <ConfirmationWrapper>
          <StatusDoneTitle>Order skickad!</StatusDoneTitle>
          <NoticeText>Din order är packad och på väg till dig.</NoticeText>
        </ConfirmationWrapper>
      );
    }

    if (s === "processing") {
      return (
        <ConfirmationWrapper>
          <StatusDoneTitle>Tack för din betalning!</StatusDoneTitle>
          <NoticeText>Vi har tagit emot din betalning och hanterar din order.</NoticeText>
        </ConfirmationWrapper>
      );
    }

    const isSwish =
      paymentMethod === "swish" ||
      String(payOrderSnapshot?.payment_method || "").toLowerCase() === "swish";

    // Legacy: manuell Swish som fortfarande väntar (on-hold).
    if (s === "on-hold" && isSwish) {
      return (
        <ConfirmationWrapper>
          <ConfirmationTitle>Orderbekräftelse</ConfirmationTitle>
          <ConfirmationText>
            Tack för din order! Ordernummer: {displayOrderNumber}
          </ConfirmationText>
          <SwishReminderSection>
            <SwishReminderTitle>Swish-betalningsuppgifter</SwishReminderTitle>
            <SwishReminderHint>Använd uppgifterna nedan i Swish-appen.</SwishReminderHint>
            <SwishOption
              amount={orderTotal != null ? String(Math.round(parseFloat(orderTotal))) : "—"}
              message={`Order ${displayOrderNumber}`}
            />
          </SwishReminderSection>
        </ConfirmationWrapper>
      );
    }

    if (paymentMethod === "stripe" || paymentMethod === "swish") {
      return (
        <ConfirmationWrapper>
          <StatusDoneTitle>Tack för din betalning!</StatusDoneTitle>
          <NoticeText>Vi har tagit emot din betalning och hanterar din order.</NoticeText>
        </ConfirmationWrapper>
      );
    }

    return (
      <ConfirmationWrapper>
        <ConfirmationTitle>Orderbekräftelse</ConfirmationTitle>
        <ConfirmationText>
          Tack för din order! Ordernummer: {displayOrderNumber}
        </ConfirmationText>
      </ConfirmationWrapper>
    );
  };

  return (
    <PageWrapper className="checkout-page">
      <CheckoutHeader className="checkout-header">
        <IconWrapper onClick={() => navigate("/")}>
          <Icon />
          <BackLabel>Fortsätt shoppa</BackLabel>
        </IconWrapper>
        <ImgWrapper>
          <Img
            src={`${process.env.PUBLIC_URL || ""}/trirebels_logo.png`}
            alt="Logo"
            onClick={() => navigate("/")}
          />
        </ImgWrapper>
        <Right />
      </CheckoutHeader>

      <Header className="checkout-title">Kassan</Header>

      {showPayRouteNotFound && (
        <PayRouteAlert>
          <ErrorBanner>Ordern hittades inte.</ErrorBanner>
          <Button as={Theme.Transparent} onClick={() => navigate("/")}>
            Till shoppen
          </Button>
        </PayRouteAlert>
      )}

      {showPayRouteError && (
        <PayRouteAlert>
          <ErrorBanner>Kunde inte hämta ordern. Försök igen senare.</ErrorBanner>
        </PayRouteAlert>
      )}

      {!showPayRouteNotFound && !showPayRouteError && (
        <>
      <CheckoutSteps
        currentStep={currentStep}
        onStepChange={handleStepChange}
      />

      {payRouteLoading && <StatusMessage>Hämtar order…</StatusMessage>}

      {currentStep === 1 &&
        (cartItemCount > 0 ? (
          <>
            {tmaShippingEnabled ? (
              <TmaCheckoutShippingStep onContinue={() => setCurrentStep(2)} />
            ) : (
              <>
                <ShippingMethodSelector />
                <ShippingOptions onClick={() => setCurrentStep(2)} />
              </>
            )}
            <Summary totalPrice={totalPrice} />
          </>
        ) : (
          <EmptyWrapper>
            <EmptyText>Här var det tomt!</EmptyText>
            <Button as={Theme.Transparent} onClick={() => navigate("/")}>
              Tillbaka till shoppen
            </Button>
            <Link to="/">Tillbaka till shoppen</Link>
          </EmptyWrapper>
        ))}

      {currentStep === 2 && (
        <OrderSummary
          onOrderCreated={onOrderCreated}
          onNextStep={() => setCurrentStep(3)}
        />
      )}

      {showPaymentStep && (
        <CheckoutPaymentStep
          orderId={orderId}
          orderNumber={displayOrderNumber || null}
          onCompleted={async (method) => {
            if (orderId) await trackPurchaseFromOrder(orderId);
            setPaymentMethod(method ?? null);
            setCurrentStep(4);
            clearCart();
            if (orderId) await refreshPayOrderSnapshot(orderId);
          }}
        />
      )}

      {currentStep === 4 &&
        orderId &&
        !showPayRouteNotFound &&
        !showPayRouteError &&
        renderPayConfirmation()}
        </>
      )}
    </PageWrapper>
  );
};

const PageWrapper = styled.div({
  minHeight: "100vh",
  marginBottom: spacing.xl,
  width: "100%",
  maxWidth: "100%",
  overflowX: "hidden",
  boxSizing: "border-box",
});

const IconWrapper = styled.div({
  display: "flex",
  alignItems: "center",
  gap: spacing.xs,
  cursor: "pointer",
  top: spacing.l,
  flex: {
    grow: "1",
    shrink: "1",
    basis: "0",
  },
  minWidth: 0,
  color: black,
  fontSize: fontSize.s,
  ":hover": {
    color: green,
  },
});

const BackLabel = styled.span({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  ...media({ maxWidth: "400px" }, {
    display: "none",
  }),
});

const EmptyWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: spacing.m,
  justifySelf: "center",
  alignItems: "center",
  marginTop: spacing.xxl,
});

const EmptyText = styled.div({
  fontSize: fontSize.xl,
});

const Icon = styled(applyClassName(GrLinkPrevious), {
  fontSize: "30px",
  flexShrink: 0,
});

const ImgWrapper = styled.div({
  flex: {
    grow: "0",
    shrink: "0",
    basis: "auto",
  },
  display: "flex",
  justifyContent: "center",
});

const Img = styled.img({
  width: "75px",
  height: "75px",
  objectFit: "cover",
  padding: { xy: spacing.xs },
  cursor: "pointer",
});

const Right = styled.div({
  flex: {
    grow: "1",
    shrink: "1",
    basis: "0",
  },
  minWidth: 0,
});

const CheckoutHeader = styled.div({
  display: "flex",
  alignItems: "center",
  padding: { x: spacing.xl, y: spacing.m },
  backgroundColor: white,
  width: "100%",
  maxWidth: "100%",
  boxSizing: "border-box",
  ...media({ maxWidth: "768px" }, {
    padding: { x: spacing.m, y: spacing.s },
  }),
});

const Header = styled.h1({
  textAlign: "center",
});

const ConfirmationWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: spacing.l,
  padding: { x: spacing.m, y: spacing.l },
});

const ConfirmationTitle = styled.h2({
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
  textAlign: "center",
});

const StatusDoneTitle = styled.h2({
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
  textAlign: "center",
  color: green,
});

const ConfirmationText = styled.p({
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
  textAlign: "center",
  fontSize: fontSize.m,
});

const SwishReminderSection = styled.div({
  width: "100%",
  maxWidth: "400px",
  margin: { top: spacing.m },
  padding: { x: spacing.l, y: spacing.l },
  backgroundColor: "#f5f5f5",
  borderRadius: "8px",
});

const SwishReminderTitle = styled.h3({
  margin: { top: 0, bottom: spacing.xs },
  fontSize: fontSize.m,
});

const SwishReminderHint = styled.p({
  margin: { top: 0, bottom: spacing.m },
  fontSize: fontSize.s,
  color: "#666",
});

const PayRouteAlert = styled.div({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: spacing.m,
  padding: { x: spacing.m, y: spacing.xl },
});

const ErrorBanner = styled.p({
  margin: { top: 0, bottom: 0 },
  textAlign: "center",
  color: "#c00",
  fontSize: fontSize.m,
});

const StatusMessage = styled.div({
  textAlign: "center",
  marginTop: spacing.m,
  marginBottom: spacing.m,
  fontSize: fontSize.l,
});

const NoticeText = styled.p({
  margin: { top: 0, bottom: 0 },
  textAlign: "center",
  fontSize: fontSize.m,
  color: "#666",
});

export default CheckoutPage;
